import React, { Fragment, ReactElement } from "react"
import { theme } from "../../../theme"
import { ProgressChip, ProgressChipState } from "../../atoms/chip"
import styled from "styled-components"
import { Check, CheckCircle } from "@mui/icons-material"
import { ArrowBottomIcon } from "../../../icons/ArrowBottomIcon"
import { ArrowTopIcon } from "../../../icons/ArrowTopIcon"

export type ProgressBarProps = {
  backgroundColor?: string
  progressBarItems: ProgressBarItem[]
}

export type ProgressBarItem = {
  label: string
  icon: ReactElement
  state: ProgressChipState
  beforeApprovalCheck?: boolean
  approvalTop?: string
  approvalBottom?: string
}

export const ProgressBar = (props: ProgressBarProps) => {
  const { backgroundColor, progressBarItems, ...rest } = props
  const maxWidth = 100 / progressBarItems.length

  return (
    <ProgressBarStyle $backgroundColor={backgroundColor} {...rest}>
      {progressBarItems.map((item) => (
        <Fragment key={item.label}>
          <CustomProgressChip
            key={item.state}
            icon={item.state === ProgressChipState.done ? <Check /> : item.icon}
            label={item.label}
            borderColor={backgroundColor}
            beforeApprovalCheck={item.beforeApprovalCheck}
            state={item.state}
            maxWidth={maxWidth}
          />
          {!!item.approvalBottom && (
            <>
              <ApprovalIcon />
              <BottomContainer>
                <BottomContainerRightFixed>
                  <ApprovalBottomContainer>{item.approvalBottom}</ApprovalBottomContainer>
                  <ArrowContainer>
                    <ArrowBottomIcon />
                  </ArrowContainer>
                </BottomContainerRightFixed>
              </BottomContainer>
            </>
          )}
          {!!item.approvalTop && (
            <>
              <ApprovalIcon />
              <TopContainer>
                <TopContainerRightFixed>
                  <ApprovalTopContainer>{item.approvalTop}</ApprovalTopContainer>
                  <ArrowContainer>
                    <ArrowTopIcon />
                  </ArrowContainer>
                </TopContainerRightFixed>
              </TopContainer>
            </>
          )}
        </Fragment>
      ))}
    </ProgressBarStyle>
  )
}

const ApprovalIcon = styled(CheckCircle)`
  width: 15px;
  color: ${theme.color.grey3};
`

const CustomProgressChip = styled(ProgressChip)``

// Bottom arrow placement
const BottomContainer = styled.span`
  position: relative;
  top: 20px;

  & + ${CustomProgressChip} {
    margin-left: 0 !important;
  }
`

const ApprovalTopContainer = styled.span`
  position: relative;
  bottom: ${theme.spacing.m};
  margin-right: ${theme.spacing.s};
  font-size: ${theme.fontSize.s};
  white-space: nowrap;
`

const BottomContainerRightFixed = styled.div`
  display: flex;
  position: absolute;
  right: 10px;
`

// Top arrow placement
const TopContainer = styled.span`
  position: relative;
  bottom: 38px;

  & + ${CustomProgressChip} {
    margin-left: 0 !important;
  }
`

const TopContainerRightFixed = styled.div`
  display: flex;
  position: absolute;
  right: 10px;
`

const ProgressBarStyle = styled.div<{ $backgroundColor?: string }>`
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: ${theme.spacing.xl};
  background-color: ${(p) => (p.$backgroundColor ? p.$backgroundColor : theme.color.white)};
`

const ApprovalBottomContainer = styled.span`
  position: relative;
  top: ${theme.spacing.m};
  margin-right: ${theme.spacing.s};
  font-size: ${theme.fontSize.s};
  white-space: nowrap;
`

const ArrowContainer = styled.span`
  position: static;
  float: right;
`
